
import React, { useRef, useState } from 'react';
import clsx from 'clsx';
import { createStyles, lighten, makeStyles, Theme, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@material-ui/core';
import melhorEnvioService from '../../../services/melhorEnvio';
import { useToast } from '../../../hooks/toast';
import { melhorEnvioStatus } from '../../../utils/melhorEnvioStatus';
import { FormHandles } from '@unform/core';
import { OptionType } from '../../Historico';

export interface TableData {
  orderLabelId: string;
  id: number;
  price: string;
  customerName: string;
  orderLabelStatus: string;
  tracking: string;
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = 'asc' | 'desc';

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
}

const headCells: HeadCell[] = [
  { id: 'orderLabelId', numeric: false, disablePadding: true, label: 'ID' },
  { id: 'id', numeric: true, disablePadding: false, label: 'Pedido' },
  { id: 'orderLabelStatus', numeric: false, disablePadding: false, label: 'Status' },
  { id: 'tracking', numeric: false, disablePadding: false, label: 'Rastreio' },
  { id: 'customerName', numeric: false, disablePadding: false, label: 'Nome' },
  { id: 'price', numeric: true, disablePadding: false, label: 'Frete' },
  { id: '', numeric: false, disablePadding: false, label: 'Cancelamento' },
];

interface EnhancedTableHeadProps {
  classes: ReturnType<typeof useStyles>;
  numSelected: number;
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableHeadProps) {
  const { classes, onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'default'}
            sortDirection={
              orderBy === headCell.id ? order : false
            }
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    highlight:
      theme.palette.type === 'light'
        ? {
            color: theme.palette.secondary.main,
            backgroundColor: lighten(theme.palette.secondary.light, 0.85),
          }
        : {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.secondary.dark,
          },
    title: {
      flex: '1 1 100%',
    },
  }),
);

interface EnhancedTableToolbarProps {
  numSelected: number;
}

const EnhancedTableToolbar = (props: EnhancedTableToolbarProps) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
          {numSelected} selecionada(s)
        </Typography>
      ) : (
        <Typography className={classes.title} variant="h6" id="tableTitle" component="div">
          Etiquetas
        </Typography>
      )}
    </Toolbar>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      marginTop: '20px'
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    cancelButton: {
      color: '#ffffff',
      backgroundColor: '#f44336',
      zIndex: 2,
      '&:hover': {
        backgroundColor: '#e57373',
      },
      '&:disabled': {
        backgroundColor: '#DDD',
      },
    },
  }),
);

interface EnhancedTableProps {
  rows: TableData[];
  selected: string[];
  setSelected: (selected: string[]) => void;
  updateData: () => void;
  selectedBranch: OptionType;
}

export default function EnhancedTable({ rows, selected, setSelected, updateData, selectedBranch }: EnhancedTableProps) {
  const classes = useStyles();

  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [open, setOpen] = useState(false);
  const [cancelDescription, setCancelDescription] = useState('');
  const [selectedIdCancel, setSelectedIdCancel] = useState('');

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.orderLabelId);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event: React.MouseEvent<unknown>, orderLabelId: string) => {
    const selectedIndex = selected.findIndex((item) => item.localeCompare(orderLabelId) === 0);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, orderLabelId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setCancelDescription('');
    setOpen(false);
  };

  function handleCancel(id: string) {
    handleCloseDialog();
    melhorEnvioService.cancelTag(id, cancelDescription, selectedBranch.id).then((res) => {
      addToast({
        title: `Etiqueta cancelada com sucesso`,
        type: 'success',
      });
      updateData();
    }).catch((error) => {
      if(error.response && error.response.data){
        addToast({
          title: error.response.data.message,
          type: 'error',
        });
      } else {
        addToast({
          title: 'Ocorreu um erro no cancelamento da etiqueta',
          type: 'error',
        });
      }
    });
    setSelectedIdCancel('');
  }

  const isSelected = (orderLabelId: string) => selected.findIndex((item) => item.localeCompare(orderLabelId) === 0) !== -1;

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size='medium'
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows.map((row) => ({...row})), getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row.orderLabelId);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.orderLabelId}
                      selected={isItemSelected}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                          onClick={(event) => handleClick(event, row.orderLabelId)}
                        />
                      </TableCell>
                      <TableCell component="th" id={labelId} scope="row" padding="none">
                        {row.orderLabelId}
                      </TableCell>
                      <TableCell align="right">{row.id}</TableCell>
                      <TableCell align="left">{melhorEnvioStatus(row.orderLabelStatus)}</TableCell>
                      <TableCell align="left">{row.tracking}</TableCell>
                      <TableCell align="left">{row.customerName}</TableCell>
                      <TableCell align="right">{row.price}</TableCell>
                      <TableCell align="center">
                        {row.orderLabelStatus !== 'canceled' && row.orderLabelStatus !== 'delivered' && (
                          <Button 
                            onClick={() => {
                              setSelectedIdCancel(row.orderLabelId);
                              handleOpenDialog();
                            }}
                            className={classes.cancelButton}
                          >
                            Cancelar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="Pedidos por página:"
        />
        {selectedIdCancel && (
          <Dialog
            open={open}
            onClose={handleCloseDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{"Cancelamento"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Você realmente deseja cancelar esta estiqueta?
              </DialogContentText>
              <TextField
                autoFocus
                margin="dense"
                id="description"
                label="Motivo do cancelamento"
                type="description"
                value={cancelDescription}
                onChange={(event) => setCancelDescription(event.currentTarget.value)}
                fullWidth
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="primary">
                Cancelar
              </Button>
              <Button 
                onClick={() => {handleCancel(selectedIdCancel)}}
                color="primary"
              >
                Confirmar
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Paper>
    </div>
  );
}